'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { ContentBreakpointContext } from '../context';
import type { ContentBreakpoint } from '../context';

/**
 * Maps a content width to a Tailwind container-scale tier, the same stops used by the
 * `@sm/main:`-style container variants, so JS and CSS respond at identical thresholds.
 */
const getContentBreakpoint = (width: number): ContentBreakpoint => {
  if (width >= 1280) {
    return '7xl';
  }
  if (width >= 1152) {
    return '6xl';
  }
  if (width >= 1024) {
    return '5xl';
  }
  if (width >= 896) {
    return '4xl';
  }
  if (width >= 768) {
    return '3xl';
  }
  if (width >= 672) {
    return '2xl';
  }
  if (width >= 576) {
    return 'xl';
  }
  if (width >= 512) {
    return 'lg';
  }
  if (width >= 448) {
    return 'md';
  }
  if (width >= 384) {
    return 'sm';
  }
  if (width >= 320) {
    return 'xs';
  }
  if (width >= 288) {
    return '2xs';
  }
  return '3xs';
};

/**
 * Observes an element's inline size and maps it to a container-scale tier. Returns null until
 * the element is measured, which keeps it SSR-safe.
 */
const useElementBreakpoint = element => {
  const $ = _c(4);
  let t0;
  if ($[0] !== element) {
    t0 = onResize => {
      if (!element) {
        return _temp;
      }
      const observer = new ResizeObserver(onResize);
      observer.observe(element);
      return () => observer.disconnect();
    };
    $[0] = element;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const subscribe = t0;
  let t1;
  if ($[2] !== element) {
    t1 = () => element ? getContentBreakpoint(element.getBoundingClientRect().width) : null;
    $[2] = element;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return React.useSyncExternalStore(subscribe, t1, _temp2);
};

/**
 * Container-scale tier of the nearest SidebarInset: the viewport width minus all open sidebars.
 * Returns null outside a SidebarInset or before the first measurement, so callers should fall
 * back to viewport-based detection in that case.
 */
const useContentBreakpoint = () => {
  return React.useContext(ContentBreakpointContext);
};
export { useContentBreakpoint, useElementBreakpoint };
function _temp() {}
function _temp2() {
  return null;
}